/**
 * FilterBar v2 pure model (PRD §4.11, T-37; DESIGN_SYSTEM.md §12.1): which
 * filters are active, their chip text, which ones may sit inline, and the
 * type-to-filter search of a long option list (`kind: 'search-select'`).
 * No React, unit-tested (`src/utils/__tests__/filterModel.test.ts`).
 */
import { normalizeForSearch } from '../ui/customerPickerModel';

export type FilterKind = 'pills' | 'select' | 'search-select' | 'dateRange';

export interface FilterOptionLike {
  value: string;
  label: string;
}

/** The parts of a filter definition the model needs. */
export interface FilterLike {
  kind?: FilterKind;
  value?: string;
  options?: readonly FilterOptionLike[];
  from?: string;
  to?: string;
}

export const isRangeFilter = (f: FilterLike) => f.kind === 'dateRange';

/** A filter narrows the list: an option is picked, or either end of a range is set. */
export function isFilterActive(f: FilterLike): boolean {
  if (isRangeFilter(f)) return !!(f.from || f.to);
  return !!f.value;
}

/** How many of these filters are active (the "סינון (n)" count). */
export function activeFilterCount(filters: readonly FilterLike[]): number {
  return filters.reduce((n, f) => n + (isFilterActive(f) ? 1 : 0), 0);
}

/** The label of the picked option (the raw value when it is not in the list, e.g. a linked id). */
export function optionLabel(f: FilterLike, value: string): string {
  return f.options?.find((o) => o.value === value)?.label ?? value;
}

export interface RangeWords {
  /** "{{from}} – {{to}}" */
  both: (from: string, to: string) => string;
  /** "מ־{{from}}" */
  since: (from: string) => string;
  /** "עד {{to}}" */
  until: (to: string) => string;
}

/** The value part of a range chip: both ends, or the one that is set. */
export function rangeText(from: string, to: string, words: RangeWords, format: (v: string) => string = (v) => v): string {
  if (from && to) return from === to ? format(from) : words.both(format(from), format(to));
  if (from) return words.since(format(from));
  if (to) return words.until(format(to));
  return '';
}

/**
 * The first `max` (≤ 2) filters that can be drawn inline as a select. A date
 * range or a long searchable list never sits inline; it stays in the panel.
 */
export function splitInline<F extends FilterLike>(filters: readonly F[], max: number): { inline: F[]; panel: F[] } {
  const limit = Math.max(0, Math.min(max, 2));
  const inline: F[] = [];
  const panel: F[] = [];
  for (const f of filters) {
    const canInline = !f.kind || f.kind === 'pills' || f.kind === 'select';
    if (canInline && inline.length < limit) inline.push(f);
    else panel.push(f);
  }
  return { inline, panel };
}

/**
 * Options matching a typed query (`search-select`): labels that start with
 * it first, then a word that starts with it, then labels containing every
 * typed word; ties keep the given order. At most `limit`, plus the total.
 */
export function searchOptions<O extends FilterOptionLike>(options: readonly O[], query: string, limit = 50): { rows: O[]; total: number } {
  const q = normalizeForSearch(query);
  if (!q) return { rows: options.slice(0, limit), total: options.length };
  const words = q.split(' ');
  const scored: { o: O; rank: number; i: number }[] = [];
  options.forEach((o, i) => {
    const label = normalizeForSearch(o.label);
    let rank = -1;
    if (label.startsWith(q)) rank = 0;
    else if (label.split(' ').some((w) => w.startsWith(q))) rank = 1;
    else if (words.every((w) => label.includes(w))) rank = 2;
    if (rank >= 0) scored.push({ o, rank, i });
  });
  scored.sort((a, b) => a.rank - b.rank || a.i - b.i);
  return { rows: scored.slice(0, limit).map((s) => s.o), total: scored.length };
}
